/**
 * Impersonation mode: an ops person (platform:impersonate in the ops console)
 * opened this tab with a short-lived token minted by ops-service whose `sub` is
 * the impersonated user. The token lives ONLY in this tab (sessionStorage), is
 * never written to localStorage and never mixed with the Auth0 SDK cache, so a
 * real admin session in another tab is untouched.
 */
const KEY = 'gs_impersonation';

export type Impersonation = {
    token: string;
    /** epoch seconds */
    exp: number;
    sub: string;
    targetEmail: string;
    actorEmail: string;
};

type Payload = {
    sub?: string;
    exp?: number;
    act?: { sub?: string; email?: string };
    'https://getservice.io/impersonation'?: { target?: string };
};

function decodePayload(token: string): Payload | null {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    try {
        const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const json = decodeURIComponent(
            atob(b64).split('').map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
        );
        return JSON.parse(json) as Payload;
    } catch {
        return null;
    }
}

/** Parses and validates the token shape locally (the backend is the real check). */
export function parseImpersonationToken(token: string): Impersonation | null {
    const p = decodePayload(token);
    if (!p || !p.sub || !p.exp || !p.act) return null;
    if (p.exp * 1000 <= Date.now()) return null;
    return {
        token,
        exp: p.exp,
        sub: p.sub,
        targetEmail: p['https://getservice.io/impersonation']?.target || '',
        actorEmail: p.act.email || '',
    };
}

export function startImpersonation(imp: Impersonation): void {
    try {
        sessionStorage.setItem(KEY, JSON.stringify(imp));
    } catch {
        /* storage unavailable: the caller shows an error */
    }
    window.dispatchEvent(new Event(IMPERSONATION_EVENT));
}

export function getImpersonation(): Impersonation | null {
    try {
        const raw = sessionStorage.getItem(KEY);
        if (!raw) return null;
        const imp = JSON.parse(raw) as Impersonation;
        if (!imp?.token || !imp.exp) return null;
        return imp;
    } catch {
        return null;
    }
}

export function isImpersonationExpired(imp: Impersonation): boolean {
    return imp.exp * 1000 <= Date.now();
}

/** Fired on this window whenever the impersonation session starts or ends. */
export const IMPERSONATION_EVENT = 'gs:impersonation-changed';

export function clearImpersonation(): void {
    try {
        sessionStorage.removeItem(KEY);
    } catch {
        /* ignore */
    }
    window.dispatchEvent(new Event(IMPERSONATION_EVENT));
}
